import { motion } from "framer-motion";
import { Box } from "@mui/material";

export default function AiAnimatedIcon() {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "24px",
        height: "24px",
        position: "relative",
      }}
    >
      <motion.svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        style={{ width: "100%", height: "100%", display: "block" }}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ duration: 0.8, type: "spring" }}
      >
        {/* Antenna */}
        <motion.path
          d="M12 2.75V4.75"
          stroke="url(#gradient1)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            y: [0, -2, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Left antenna connector */}
        <motion.path
          d="M3 8.75C2.58579 8.75 2.25 9.08579 2.25 9.5V10.5C2.25 10.9142 2.58579 11.25 3 11.25"
          stroke="url(#gradient2)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            x: [-1, 0, -1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Right antenna connector */}
        <motion.path
          d="M21 11.25C21.4142 11.25 21.75 10.9142 21.75 10.5V9.5C21.75 9.08579 21.4142 8.75 21 8.75"
          stroke="url(#gradient2)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            x: [1, 0, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Mouth - with speaking animation */}
        <motion.path
          d="M9.75 14.5H14.25"
          stroke="url(#gradient3)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            scaleX: [1, 1.1, 1],
            y: [0, 0.5, 0],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Left eye - with blinking */}
        <motion.circle
          cx="8.5"
          cy="10"
          r="1.5"
          fill="url(#gradient4)"
          animate={{
            scaleY: [1, 0.1, 1, 1, 1],
            opacity: [1, 0.8, 1, 1, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            times: [0, 0.1, 0.2, 0.8, 1],
            ease: "easeInOut",
          }}
        />

        {/* Right eye - with blinking */}
        <motion.circle
          cx="15.5"
          cy="10"
          r="1.5"
          fill="url(#gradient4)"
          animate={{
            scaleY: [1, 0.1, 1, 1, 1],
            opacity: [1, 0.8, 1, 1, 1],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            times: [0, 0.1, 0.2, 0.8, 1],
            ease: "easeInOut",
          }}
        />

        {/* Main body - with breathing effect */}
        <motion.path
          d="M20.75 16.25V6.75C20.75 5.64543 19.8546 4.75 18.75 4.75H5.25C4.14543 4.75 3.25 5.64543 3.25 6.75V16.25C3.25 17.9069 4.59315 19.25 6.25 19.25H17.75C19.4069 19.25 20.75 17.9069 20.75 16.25Z"
          stroke="url(#gradient5)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          animate={{
            scale: [1, 1.02, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />

        {/* Gradients for colorful effect */}
        <defs>
          <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
            <motion.stop
              offset="0%"
              stopColor="#6366f1"
              animate={{ stopColor: ["#6366f1", "#8b5cf6", "#6366f1"] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
            <motion.stop
              offset="100%"
              stopColor="#8b5cf6"
              animate={{ stopColor: ["#8b5cf6", "#6366f1", "#8b5cf6"] }}
              transition={{ duration: 3, repeat: Infinity }}
            />
          </linearGradient>

          <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>

          <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ec4899" />
            <stop offset="100%" stopColor="#f43f5e" />
          </linearGradient>

          <radialGradient id="gradient4">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#f59e0b" />
          </radialGradient>

          <linearGradient id="gradient5" x1="0%" y1="0%" x2="100%" y2="100%">
            <motion.stop
              offset="0%"
              stopColor="#3b82f6"
              animate={{ stopColor: ["#3b82f6", "#6366f1", "#3b82f6"] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
            <motion.stop
              offset="100%"
              stopColor="#8b5cf6"
              animate={{ stopColor: ["#8b5cf6", "#ec4899", "#8b5cf6"] }}
              transition={{ duration: 4, repeat: Infinity }}
            />
          </linearGradient>
        </defs>
      </motion.svg>

      {/* Floating particles for intelligence effect */}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            width: "2px",
            height: "2px",
            borderRadius: "50%",
            backgroundColor: "#6366f1",
            opacity: 0.6,
          }}
          initial={{
            x: Math.random() * 40 - 20,
            y: Math.random() * 40 - 20,
          }}
          animate={{
            x: [Math.random() * 40 - 20, Math.random() * 40 - 20, Math.random() * 40 - 20],
            y: [Math.random() * 40 - 20, Math.random() * 40 - 20, Math.random() * 40 - 20],
            opacity: [0.2, 0.6, 0.2],
          }}
          transition={{
            duration: 5 + i,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </Box>
  );
}
